import { computedFrom } from "aurelia-framework";
import { BaseRatingEntity } from "./base-rating-entity";
import * as moment from "moment";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { LookupValueQuery } from "@rightship-tech/common-ui/constants/lookup.constants";
import { LookupValue, LookupValueRequest } from "@rightship-tech/common-ui/models/lookup.models";
import { NotificationClientService } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { LookupClientService } from "@rightship-tech/common-ui/services/lookups/lookup-client.service";
import { DueDiligenceClientModel, RatingsSummaryModel, VetStatusHistoryClientModel } from "../../../../models/due-diligence.models";
import { DueDiligenceDocuments } from "../tabs/documents/due-diligence-documents";
import { VesselApi } from "../../../../apis/vessel.api";
import { SignalFunctions } from "../../../../constants/signal.constants";
import { SafetyRatingApi } from "../../../../apis/safety-rating.api";

export abstract class BaseVettingEntity extends BaseRatingEntity<DueDiligenceClientModel> {

    public readonly: boolean = true;
    public vetStatuses: LookupValue[];
    public canChangeVetStatus: boolean;
    public ratingModel: RatingsSummaryModel;

    @computedFrom("item", "item.built")
    public get age() {
        if (this.item) {
            if (this.item.built) {
                return (Math.floor(moment().diff(this.item.built, 'year', true) * 10) / 10).toFixed(1) + " y/o"
            } else {
                return ""
            }
        } else {
            return null;
        }
    }

    abstract get isClosed();

    constructor (protected userClaimsService: UserClaimsService,
        protected notificationService: NotificationClientService,
        protected vetDocuments: DueDiligenceDocuments,
        protected signalClientService: SignalClientService,
        protected userClientService: UserClientService,
        protected vesselApi: VesselApi,
        lookupClientService: LookupClientService,
        protected safetyRatingApi: SafetyRatingApi) {
        super(signalClientService, vesselApi, safetyRatingApi);

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.VetStatuses;
        request.parameters = "1,3";
        lookupClientService.getLookup(request).then(x => {
            this.vetStatuses = x.activeValues;
        });
    }

    reloadItemFunc: (() => Promise<void>) = async () => {
        this.item = await this.getItem(this.getItemId());
        setTimeout(() => {
            this.refreshTabs();
        });
    }

    attached() {
        super.attached();
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
        this.refreshRatingModel({ vesselIds: [this.item.vesselId] });
    }

    detached() {
        super.detached();
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.RightShipInspection), this.refreshRightShipInspectionStatus);
    }

    private refreshRightShipInspectionStatus = (params: {vesselId:number}) => {
        if (params && this.item.vesselId == params.vesselId) {
            this.loadRatingModel();
        }
    }

    refreshVet = async (param: { vetId: number, entityId: number }) => {
        const id = this.getItemId();
        const updatedVetId = param.entityId ?? param.vetId;
        if (id == updatedVetId) {
            this.item = await this.getItem(id);
            setTimeout(() => {
                this.refreshTabs();
            });
        }
    };

    refreshVetStatus = (param: {vesselId:number, entityId:number, additionalMeta:any, statusId:number, userId: number, userName:string, statusSnippet:string, comment:string}) => {
        if (this.item.vetId == param.entityId) {
            if (param.additionalMeta) {
                this.item.starRatingAtVetOutcome = param.additionalMeta.safetyRatingAtVetOutcome;
                this.item.safetyRatingTypeAtVetOutcome = param.additionalMeta.safetyRatingTypeAtVetOutcome;
                this.item.ratingAtVetOutcome = param.additionalMeta.ghgRatingAtVetOutcome;
                this.item.ratingPlusAtVetOutcome = param.additionalMeta.ghgRatingPlusAtVetOutcome;
                this.item.isRatingVerifiedAtVetOutcome = param.additionalMeta.isGhgRatingVerifiedAtVetOutcome;
                this.item.docSubscoreAtVetOutcome = param.additionalMeta.docSubscoreAtVetOutcome;
            }

            const previous = this.item.vetStatusHistory[0] ? this.item.vetStatusHistory[0].statusId : null;
            if (!param.statusId) return;

            const vetStatus = this.vetStatuses.filter(x => x.id == param.statusId)[0];

            if (vetStatus) {
                this.item.vetStatus = vetStatus.name;
                this.item.vetStatusId = vetStatus.id;
                this.item.vetStatusHistory.splice(0, 0, <VetStatusHistoryClientModel>{
                    previousStatusId: previous,
                    createdDate: new Date(),
                    status: vetStatus.name,
                    statusId: vetStatus.id,
                    user: param.userName && param.userId > 0 ? param.userName : "System",
                    subtaskTypes: [],
                    comment: param.statusSnippet,
                    fullComment: param.comment,
                    createdBy: param.userName && param.userId > 0 ? param.userName : "System"
                });
            }
            this.refreshVesselRelatedData();
        }
        else if (param.vesselId == this.item.vesselId)
        {
            this.refreshVesselRelatedData();
        }
    }

    protected refreshVesselRelatedData() {}

    private refreshRatingModel = async (param) => {
        if (param && param.vesselIds && param.vesselIds.filter(x => x == this.item.vesselId).length >= 1) {
            this.loadRatingModel();
        }
    }

    async loadRatingModel() {
        try {
            const response = await this.safetyRatingApi.getRatingsModel(this.item.vesselId);
            this.ratingModel = response;
        } catch (e) {
            console.log("Failed to load rating model", e);
        }
    }
}